import { Link, Typography, Chip, alpha } from "@mui/material";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Policy, PolicyStatus } from "@/types";
import { formatYearRange } from "@/utils/formatters/dateFormatter";

interface PolicyFieldProps {
  /** Single policy to display */
  policy?: Policy | null;
  /** Array of policies — displays the first one */
  policies?: Policy[];
  /** Max width for the carrier name text (default: 140) */
  maxWidth?: number;
}

/** Extracts the carrier name from enriched vendor_company_name, source_data, or fallback */
export const getCarrierLabel = (policy: Policy): string => {
  return (
    (policy as Record<string, string>).vendor_company_name || policy.source_data?.stop_loss_carrier || "No Carrier"
  );
};

/**
 * Shared policy field component that displays a policy as a clickable link
 * with carrier name + year range chip. Used in claimant lists, reimbursement lists, etc.
 */
export const PolicyField: FC<PolicyFieldProps> = ({ policy: singlePolicy, policies, maxWidth = 140 }) => {
  const policy = singlePolicy || policies?.[0];

  if (!policy) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const isActive = policy.status === PolicyStatus.ACTIVE;
  const policyYear = formatYearRange(policy.effective_start_date, policy.effective_end_date);
  const carrierLabel = getCarrierLabel(policy);

  return (
    <Link
      component={RouterLink}
      to={`/policies/${policy.id}/show`}
      underline="none"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        transition: "all 0.15s ease",
        "&:hover": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          color: isActive ? "text.primary" : "text.secondary",
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {carrierLabel}
      </Typography>
      {policyYear && (
        <Chip
          label={isActive ? policyYear : `${policyYear} (Expired)`}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: isActive ? (theme) => alpha(theme.palette.success.main, 0.1) : "grey.100",
            color: isActive ? "success.dark" : "text.secondary",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      )}
    </Link>
  );
};
